import React, { useState } from "react";
import axios from "axios";
import backgroundImage from "../assets/images/bg.png";
import logo from "../assets/images/logoadm.png";
import {
  FiEye,
  FiEyeOff,
  FiLock,
  FiUser,
  FiCheckCircle,
  FiShield,
  FiShoppingBag,
  FiActivity,
  FiTrendingUp
} from "react-icons/fi";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Dynamic greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 4 && hour < 11) return "Selamat Pagi ☀️";
    if (hour >= 11 && hour < 15) return "Selamat Siang 🌤️";
    if (hour >= 15 && hour < 18) return "Selamat Sore 🌅";
    return "Selamat Malam 🌙";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/user/log-in`,
        {
          username_or_email: email,
          password: password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = response?.data?.data;

      if (response.status === 200 && data) {
        const { full_name, employee_id, token, role, outlet_id } = data;

        localStorage.setItem("full_name", full_name);
        localStorage.setItem("employee_id", employee_id);
        localStorage.setItem("token", token);
        localStorage.setItem("role", role);
        if (outlet_id) {
          localStorage.setItem("id_outlet", outlet_id);
        }

        setShowSuccessModal(true);
        setTimeout(() => {
          window.location.href = "/";
        }, 1500);
      } else {
        setError("Login gagal. Silakan periksa kredensial Anda.");
        Swal.fire({
          icon: "error",
          title: "Login Gagal",
          text: "Username/Email atau password tidak cocok.",
          customClass: { popup: "rounded-3xl" },
        });
      }
    } catch (err) {
      console.error("Error saat login:", err);
      const errMsg =
        err.response?.data?.message ||
        "Terjadi kesalahan saat login. Silakan periksa username/password Anda.";
      setError(errMsg);
      Swal.fire({
        icon: "error",
        title: "Login Gagal",
        text: errMsg,
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex items-center justify-center min-h-screen w-screen overflow-hidden bg-cover bg-center bg-no-repeat font-sans"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      {/* Premium Dark Gradient Overlay with Ambient Glow */}
      <div className="absolute inset-0 bg-gradient-to-tr from-gray-950/80 via-emerald-950/50 to-gray-900/80 backdrop-blur-[3px]" />

      {/* Floating Animated Background Glow Circles */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/4 left-10 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute bottom-10 right-10 w-[450px] h-[450px] bg-teal-500/20 rounded-full blur-3xl pointer-events-none"
      />

      <div className="relative z-10 container mx-auto flex flex-col lg:flex-row items-center justify-between px-6 md:px-16 py-10 gap-12">
        {/* Left Side Branding & Value Props */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:w-1/2 text-center lg:text-left space-y-6"
        >
          <div className="inline-block p-2 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl">
            <img
              src={logo}
              alt="Djaya Medika Logo"
              className="w-72 max-w-full h-auto mx-auto lg:mx-0 drop-shadow-2xl"
            />
          </div>

          <div className="space-y-3 text-white">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
              Sistem Manajemen <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-300">
                Apotek Djaya Medika
              </span>
            </h1>
            <p className="text-gray-300 text-sm md:text-base max-w-lg leading-relaxed">
              Platform manajemen operasional apotek modern, kasir POS terintegrasi, kontrol stok obat real-time, dan audit rekapitulasi keuangan.
            </p>
          </div>

          {/* Interactive Feature Pills */}
          <div className="grid grid-cols-2 gap-3 max-w-md pt-2">
            <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 text-white flex items-center gap-2.5">
              <div className="p-2 bg-emerald-500/20 text-emerald-300 rounded-xl">
                <FiShoppingBag className="w-4 h-4" />
              </div>
              <span className="text-xs font-bold">Kasir POS Cepat</span>
            </div>

            <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 text-white flex items-center gap-2.5">
              <div className="p-2 bg-teal-500/20 text-teal-300 rounded-xl">
                <FiActivity className="w-4 h-4" />
              </div>
              <span className="text-xs font-bold">Stok & Expired Alert</span>
            </div>

            <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 text-white flex items-center gap-2.5">
              <div className="p-2 bg-cyan-500/20 text-cyan-300 rounded-xl">
                <FiTrendingUp className="w-4 h-4" />
              </div>
              <span className="text-xs font-bold">Laporan Real-Time</span>
            </div>

            <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/15 text-white flex items-center gap-2.5">
              <div className="p-2 bg-emerald-500/20 text-emerald-300 rounded-xl">
                <FiShield className="w-4 h-4" />
              </div>
              <span className="text-xs font-bold">Audit Log Keamanan</span>
            </div>
          </div>
        </motion.div>

        {/* Right Side Premium Glassmorphism Card */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="w-full lg:w-[420px] bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl p-8 md:p-9 border border-white/50 space-y-6"
        >
          <div className="text-center space-y-1">
            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 font-bold text-[11px] rounded-full uppercase tracking-wider border border-emerald-100">
              Djaya Medika Portal
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight pt-2">
              {getGreeting()}
            </h2>
            <p className="text-xs text-gray-500 font-semibold">
              Masukkan kredensial Anda untuk melanjutkan
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleLogin}>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 text-xs font-bold text-center shadow-sm"
              >
                {error}
              </motion.div>
            )}

            {/* Username or Email Input */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="text-gray-700 font-extrabold uppercase text-[10px] tracking-wider block"
              >
                Username / Email Akun
              </label>
              <div className="relative flex items-center group">
                <FiUser className="absolute left-3.5 text-gray-400 group-focus-within:text-emerald-600 w-4 h-4 transition-colors" />
                <input
                  id="email"
                  type="text"
                  placeholder="Username atau email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3.5 bg-gray-50/80 border border-gray-200 rounded-2xl text-xs text-gray-900 font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-inner"
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="text-gray-700 font-extrabold uppercase text-[10px] tracking-wider block"
              >
                Password Kunci
              </label>
              <div className="relative flex items-center group">
                <FiLock className="absolute left-3.5 text-gray-400 group-focus-within:text-emerald-600 w-4 h-4 transition-colors" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Masukkan password Anda..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3.5 bg-gray-50/80 border border-gray-200 rounded-2xl text-xs text-gray-900 font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-inner"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Checkbox Remember Me */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center text-xs font-semibold text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="mr-2 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300 w-4 h-4"
                />
                Ingat Perangkat Saya
              </label>
            </div>

            {/* Submit CTA Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-extrabold text-xs rounded-2xl shadow-xl shadow-emerald-600/25 transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <AiOutlineCheckCircle className="text-lg" />
                  <span>Masuk Ke Dashboard Apotek</span>
                </>
              )}
            </motion.button>
          </form>

          {/* Secure Footer Badge */}
          <div className="pt-2 text-center">
            <p className="text-[10px] text-gray-400 flex items-center justify-center gap-1">
              <FiShield className="text-emerald-600" /> Terproteksi Enkripsi SSL Apotek Djaya Medika
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Sukses Login */}
      <AnimatePresence>
        {showSuccessModal && (
          <div className="fixed inset-0 bg-gray-950/70 backdrop-blur-md flex justify-center items-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 15 }}
              transition={{ duration: 0.25 }}
              className="bg-white p-7 rounded-3xl shadow-2xl text-center max-w-xs w-full space-y-4 border border-emerald-100"
            >
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <FiCheckCircle className="w-10 h-10" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-black text-gray-900">
                  Autentikasi Berhasil!
                </h3>
                <p className="text-xs text-gray-500 font-semibold">
                  Selamat datang kembali. Mengalihkan ke halaman utama dashboard...
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LoginPage;
